import React, { useMemo } from 'react';
import LoadingSequence from 'assets/Loading/LoadingSequence';
import LinechartDashboard from 'views/LinechartDashboard';
import WidgetDiagnostics from './WidgetDiagnostics';
import { useWidgetFetch } from 'hooks/useWidgetFetch';
import { apiPath } from 'apiurl';

const BUCKET_FOR_RANGE = {
  lastTwentyFourHours: 'hour',
  currentMonth:        'day',
  '6m':                'month',
  '1y':                'month',
  all:                 'year',
};

function formatLabel(item, bucket) {
  const year = parseInt(item?.Year, 10);
  if (isNaN(year)) return null;
  const mo  = parseInt(item?.Month, 10) || 1;
  const day = parseInt(item?.Day,   10) || 1;
  const h   = parseInt(item?.Hour,  10) || 0;
  if (bucket === 'hour')  return new Date(year, mo - 1, day, h).toLocaleTimeString('en-US', { hour: 'numeric', hour12: true });
  if (bucket === 'day')   return new Date(year, mo - 1, day).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  if (bucket === 'month') return new Date(year, mo - 1, 1).toLocaleDateString('en-US', { month: 'short', year: 'numeric' });
  return String(year);
}

const getRangeBadge = (timeRange) => {
  const labels = {
    '1y': 'Past Year', '6m': '6 Months',
    currentMonth: 'Current Month', lastTwentyFourHours: 'Last 24 Hours',
  };
  return labels[timeRange] || 'All Time';
};

function SequencingTimeline({ timeRange }) {
  const url = `${apiPath}/api/datasize?timeRange=${timeRange}`;

  const { data, loading, error, timing } = useWidgetFetch(url, {
    ttl: 5 * 60 * 1000,
    swr: 10 * 60 * 1000,
    widgetId: 'Timeline',
  });

  const chartData = useMemo(() => {
    if (!Array.isArray(data) || data.length === 0) return [];
    const bucket = BUCKET_FOR_RANGE[timeRange] || 'year';
    return data
      .map(item => {
        const date = formatLabel(item, bucket);
        if (!date) return null;
        return {
          date,
          completed:        Number(item.Completed)        || 0,
          failed:           Number(item.Failed)           || 0,
          sizeGB:           Number(item.TotalSizeGB)      || null,
          totalCount:       Number(item.TotalOccurrences) || 0,
          processedPercent: Number(item.processedPercent) || 0,
        };
      })
      .filter(Boolean);
  }, [data, timeRange]);

  const isEmpty = !loading && !error && chartData.length === 0;

  return (
    <div className="dash-chart-card">
      <div className="dash-card-header">
        <span className="dash-card-title"><i className="bi bi-activity" />Sequencing Activity Timeline</span>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <span className="dash-card-badge">{getRangeBadge(timeRange)}</span>
          <WidgetDiagnostics timing={timing} url={url} dataCount={Array.isArray(data) ? data.length : null} />
        </div>
      </div>
      <div className="dash-chart-body">
        {loading ? (
          <LoadingSequence message="Loading chart data" size="medium" />
        ) : error ? (
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%', color: '#94a3b8', fontSize: '14px' }}>
            Unable to load timeline data
          </div>
        ) : isEmpty ? (
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%', color: '#94a3b8', fontSize: '14px' }}>
            No activity data for this period
          </div>
        ) : (
          <LinechartDashboard timeRange={timeRange} data={chartData} />
        )}
      </div>
    </div>
  );
}

export default SequencingTimeline;
